import React, { Component } from 'react';
import PropTypes from 'prop-types';
import './StackedBar.scss';
import cn from 'classnames';
import { STATUS_ICON } from './progressHelpers';
import { Icon } from 'legacy-packages/ft-cb';
import PopOver from 'chameleon/ui-stack/ui/base/popover';
import { v4 as uuidv4 } from 'uuid';

const { arrayOf, number, bool, string, shape } = PropTypes;

export const Stack = ({ value, classStatus, style, size }) => {
  if (value === 0) return null;
  let stackValue = value;
  /**
   * FY-8265: sends progress.not_started = -1 as a case
   * when a class has no students for an assigned assessment.
   * Therefore show the Stack with a stack value of 0 as default
   */
  if (value === -1) {
    stackValue = 0;
  }

  const getBarStyle = (size) => {
    let classNames =
      'flex text-white rounded-full justify-center items-center px-6';
    return size === 'large'
      ? `${classNames} h-14 bg-gray-600 w-full`
      : `${classNames} h-10 bg-gray-600 w-80`;
  };

  if (stackValue === 0) {
    return <span className={`${getBarStyle(size)}`}>{stackValue}</span>;
  }

  return (
    <div className={cn('cursor-pointer stack', classStatus)} style={style}>
      <span className="stack-value" data-test-stack-value={classStatus}>
        {stackValue}
      </span>
    </div>
  );
};

class StackedBar extends Component {
  static propTypes = {
    stacks: arrayOf(
      shape({
        status: string.isRequired,
        label: string,
        value: number.isRequired,
      }),
    ),
    showLabels: bool,
    isSmall: bool,
    alwaysSmall: bool,
    showMovedStudentIndicator: bool,
  };

  static defaultProps = {
    showLabels: false,
    isSmall: false,
    alwaysSmall: false,
    isLoading: false,
    stacks: [],
    showMovedStudentIndicator: false,
  };

  render() {
    const {
      showLabels,
      stacks,
      isSmall,
      isLoading,
      showMovedStudentIndicator,
      alwaysSmall,
    } = this.props;

    const availableStacks = stacks.filter(
      (stack) => stack.text || stack.value !== 0,
    );

    return (
      <div
        className={cn(
          'stacked-bar',
          { '--smaller': isSmall },
          { '--loading': isLoading },
        )}
      >
        <div className="bar">
          {/* single stack of size 0 when class is empty */}
          {stacks.length === 1 && stacks[0].value === 0 && (
            <Stack value={-1} size={alwaysSmall ? 'small' : 'large'} />
          )}

          {availableStacks.map((stack, index) => {
            const isFirst = index === 0;
            const isLast = index === availableStacks.length - 1;
            // because key of 'incorrect' doesn't change when you change questions, react thinks the element hasn't
            // changed even though the props have changed, so it never updates the styles.
            // This ends up leaving rounded borders in the inside stacks
            const uuid = `${uuidv4()}_${stack.status}`;
            return (
              <Stack
                key={uuid}
                classStatus={stack.status}
                value={stack.text || stack.value}
                style={{
                  flexGrow: `${stack.value === -1 ? 1 : stack.value}`,
                  borderTopLeftRadius: `${isFirst ? '20px' : 'none'}`,
                  borderBottomLeftRadius: `${isFirst ? '20px' : 'none'}`,
                  borderTopRightRadius: `${isLast ? '20px' : 'none'}`,
                  borderBottomRightRadius: `${isLast ? '20px' : 'none'}`,
                }}
              />
            );
          })}
        </div>

        {showLabels && (
          <div className="labels">
            {stacks.map((stack) => {
              const status = STATUS_ICON[stack.status];

              return (
                stack.value !== 0 && (
                  <span className="label" key={stack.status}>
                    <Icon
                      name={status.icon}
                      size="1.2em"
                      className={status.className}
                    />
                    {status.label}
                  </span>
                )
              );
            })}
            {showMovedStudentIndicator && (
              <span className="label label--moved-student-indicator">
                <span className="label-text">
                  *From student&apos;s previous class section
                </span>
                <PopOver
                  className="popoverIndicator"
                  size="medium"
                  accessibility={{
                    'aria-label': 'info',
                    'aria-describedby': 'info-tooltip',
                  }}
                  trigger={<Icon name="info" />}
                >
                  <p id="info-tooltip">
                    {`When students transfer class sections, their results on common
                    assessments (all Personal Progress Checks and any Question Bank
                    quizzes assigned to both sections), transfer with them. An
                    asterisk identifies any students who have completed your
                    assignment in a prior class section, with the date it was
                    submitted or scored for reference.`}
                  </p>
                </PopOver>
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default StackedBar;
